import React, { useEffect, useState, useCallback } from "react";
import axiosInstance from "shared/services/api/axiosInstance";

function normalizeTeamId(teamId) {
  if (teamId == null || teamId === "") return null;
  if (typeof teamId === "object" && teamId !== null) {
    return teamId._id ?? teamId.id ?? null;
  }
  return teamId;
}

/**
 * Flow-only breakdown: not property/cohort-as-filter — either conversion split (completed full path vs not)
 * or compare flows across selected cohorts.
 */
export default function FlowBreakdownControls({
  flowBreakdown = { mode: "none", cohortIds: [] },
  onFlowBreakdownChange,
  teamId,
}) {
  const tid = normalizeTeamId(teamId);
  const [cohorts, setCohorts] = useState([]);
  const [cohortsLoading, setCohortsLoading] = useState(false);

  const mode = flowBreakdown.mode || "none";
  const cohortIds = Array.isArray(flowBreakdown.cohortIds) ? flowBreakdown.cohortIds : [];

  const fetchCohorts = useCallback(async () => {
    if (!tid) {
      setCohorts([]);
      return;
    }
    setCohortsLoading(true);
    try {
      const res = await axiosInstance.get("/cohorts", { params: { team_id: tid } });
      const raw = res.data?.cohorts || [];
      setCohorts(raw.map((c) => ({ id: c.id, name: c.name || `Cohort ${c.id}` })));
    } catch {
      setCohorts([]);
    } finally {
      setCohortsLoading(false);
    }
  }, [tid]);

  useEffect(() => {
    fetchCohorts();
  }, [fetchCohorts]);

  const setMode = (next) => {
    if (next === "cohorts") {
      onFlowBreakdownChange({ mode: "cohorts", cohortIds });
    } else if (next === "conversion") {
      onFlowBreakdownChange({ mode: "conversion", cohortIds: [] });
    } else {
      onFlowBreakdownChange({ mode: "none", cohortIds: [] });
    }
  };

  const toggleCohort = (id) => {
    const n = Number(id);
    const next = cohortIds.includes(n) ? cohortIds.filter((x) => x !== n) : [...cohortIds, n];
    onFlowBreakdownChange({ mode: "cohorts", cohortIds: next });
  };

  const modeBtn = (key, label) => (
    <button
      key={key}
      type="button"
      onClick={() => setMode(key)}
      className="flex-1 px-2 py-1.5 text-[11px] font-medium transition-colors rounded-md"
      style={{
        background: mode === key ? "color-mix(in srgb, var(--brand-primary) 10%, transparent)" : "transparent",
        color: mode === key ? "var(--text-primary)" : "var(--text-muted)",
      }}
    >
      {label}
    </button>
  );

  return (
    <div className="mb-4 space-y-3">
      <div className="flex rounded-lg border p-0.5 gap-0.5" style={{ borderColor: "var(--border-default)" }}>
        {modeBtn("none", "None")}
        {modeBtn("conversion", "Conversion")}
        {modeBtn("cohorts", "Cohorts")}
      </div>

      {mode === "cohorts" && (
        <div className="space-y-2">
          <p className="text-[11px] leading-snug" style={{ color: "var(--text-muted)" }}>
            Select one or more cohorts. The flow map shows one journey with each step split by cohort.
          </p>
          <div
            className="max-h-[200px] overflow-y-auto rounded-lg border px-2 py-1.5 space-y-1"
            style={{ borderColor: "var(--border-default)" }}
          >
            {cohortsLoading && (
              <div className="text-[11px] py-2" style={{ color: "var(--text-muted)" }}>
                Loading cohorts…
              </div>
            )}
            {!cohortsLoading && cohorts.length === 0 && (
              <div className="text-[11px] py-2" style={{ color: "var(--text-muted)" }}>
                No cohorts yet. Create cohorts from the Cohorts page.
              </div>
            )}
            {!cohortsLoading &&
              cohorts.map((c) => (
                <label
                  key={c.id}
                  className="flex items-center gap-2 cursor-pointer py-1 px-1 rounded hover:bg-surface-sunken"
                >
                  <input
                    type="checkbox"
                    checked={cohortIds.includes(Number(c.id))}
                    onChange={() => toggleCohort(c.id)}
                    className="rounded border-line-strong"
                  />
                  <span className="text-xs truncate" style={{ color: "var(--text-primary)" }}>
                    {c.name}
                  </span>
                </label>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
